import React, { useMemo, useState } from "react";
import { Plus, Trash2, Calculator, MessageCircle, Info } from "lucide-react";
import { estimatorCategories, conditions, wa } from "../mock";

const inr = (n) => "\u20b9" + Math.round(n).toLocaleString("en-IN");

export default function Estimator() {
  const [rows, setRows] = useState([
    { cat: "laptop", cond: "working", qty: 10 }
  ]);

  const update = (i, key, val) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  const addRow = () => setRows((r) => [...r, { cat: "desktop", cond: "working", qty: 5 }]);
  const delRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const { min, max, summary } = useMemo(() => {
    let mn = 0, mx = 0;
    const parts = [];
    rows.forEach((row) => {
      const c = estimatorCategories.find((x) => x.key === row.cat);
      const cond = conditions.find((x) => x.key === row.cond);
      const q = Math.max(0, Number(row.qty) || 0);
      if (!c || !cond || q === 0) return;
      mn += c.min * cond.factor * q;
      mx += c.max * cond.factor * q;
      parts.push(`${q} × ${c.label} (${cond.label})`);
    });
    return { min: mn, max: mx, summary: parts.join(", ") };
  }, [rows]);

  const waMsg = wa(
    `Hi Kapatia, I used the estimator. Assets: ${summary || "-"}. Indicative estimate: ${inr(min)}–${inr(max)}. Please share a firm quote and pickup details.`
  );

  return (
    <section id="estimate" data-testid="estimator-section" className="home-section" style={{ scrollMarginTop: 120 }}>
      <div className="container">
        <div className="reveal mx-auto max-w-[780px] text-center">
          <p className="eyebrow centered">Instant buyback estimate</p>
          <h2 className="mt-3 font-heading font-extrabold leading-[1.08]"
              style={{ color: "var(--green-950)", fontSize: "clamp(28px,3.6vw,42px)" }}>
            Get a ballpark value before you enquire
          </h2>
          <p className="mt-4 text-[16px]" style={{ color: "var(--muted)" }}>
            Add your assets, quantity and condition to see an indicative range instantly.
          </p>
        </div>

        <div className="reveal mx-auto mt-9 grid max-w-[1000px] gap-6 lg:grid-cols-[1fr_320px]">
          {/* Builder */}
          <div className="rounded-2xl border bg-white p-5 md:p-6" style={{ borderColor: "var(--line)", boxShadow: "var(--shadow)" }}>
            <div className="grid gap-3">
              {rows.map((row, i) => (
                <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_60px_40px] items-center gap-2 sm:grid-cols-[1fr_1fr_84px_44px] sm:gap-2.5">
                  <select value={row.cat} onChange={(e) => update(i, "cat", e.target.value)} data-testid={`estimator-cat-${i}`}
                          className="h-11 rounded-lg border bg-white px-2 font-heading text-[13px] font-semibold outline-none focus:ring-4 sm:px-3 sm:text-[14px]"
                          style={{ borderColor: "#d8c8af", color: "var(--green-950)" }}>
                    {estimatorCategories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                  <select value={row.cond} onChange={(e) => update(i, "cond", e.target.value)} data-testid={`estimator-cond-${i}`}
                          className="h-11 rounded-lg border bg-white px-2 font-heading text-[13px] font-semibold outline-none focus:ring-4 sm:px-3 sm:text-[14px]"
                          style={{ borderColor: "#d8c8af", color: "var(--green-950)" }}>
                    {conditions.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                  <input type="number" min={0} value={row.qty} onChange={(e) => update(i, "qty", e.target.value)} data-testid={`estimator-qty-${i}`}
                         className="h-11 rounded-lg border bg-white px-2 text-center font-heading text-[13px] font-semibold outline-none focus:ring-4 sm:px-3 sm:text-[14px]"
                         style={{ borderColor: "#d8c8af", color: "var(--green-950)" }} aria-label="Quantity" />
                  <button onClick={() => delRow(i)} disabled={rows.length === 1} data-testid={`estimator-del-${i}`}
                          className="grid h-11 w-10 place-items-center rounded-lg border transition-colors disabled:opacity-40 sm:w-11"
                          style={{ borderColor: "var(--line)", color: "var(--orange-700)" }} aria-label="Remove row">
                    <Trash2 size={17} />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={addRow} data-testid="estimator-add"
                    className="kp-button kp-button--compact mt-4 inline-flex items-center gap-2 rounded-full border-2 px-4 py-2.5 font-heading text-[14px] font-bold transition-transform hover:-translate-y-0.5"
                    style={{ borderColor: "var(--green-800)", color: "var(--green-900)" }}>
              <Plus size={16} /> Add another asset
            </button>
            <p className="mt-4 flex items-start gap-2 text-[12.5px]" style={{ color: "var(--muted)" }}>
              <Info size={15} className="mt-0.5 flex-shrink-0" />
              These are indicative estimates only. Final price is confirmed after physical inspection.
            </p>
          </div>

          {/* Result */}
          <div className="grid content-start gap-4 rounded-2xl p-6 text-white"
               style={{ background: "radial-gradient(circle at 90% 8%, rgba(13,106,67,0.6), transparent 40%), linear-gradient(135deg, var(--green-950), var(--green-800))", boxShadow: "0 18px 38px rgba(45,106,79,0.22)" }}>
            <span className="inline-flex w-max items-center gap-2 rounded-full px-3 py-1.5 text-[12.5px] font-bold"
                  style={{ background: "rgba(244,119,28,0.2)", color: "var(--yellow-500)" }}>
              <Calculator size={15} /> Estimated range
            </span>
            <div>
              <div className="font-heading text-[30px] font-extrabold leading-tight">{inr(min)}</div>
              <div className="text-[15px] font-semibold" style={{ color: "rgba(255,255,255,0.7)" }}>to</div>
              <div className="font-heading text-[30px] font-extrabold leading-tight">{inr(max)}</div>
            </div>
            <a href={waMsg} target="_blank" rel="noopener noreferrer" data-testid="estimator-quote"
               className="kp-button mt-1 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-heading font-bold transition-transform hover:-translate-y-0.5"
               style={{ background: "var(--yellow-500)", color: "var(--green-950)" }}>
              <MessageCircle size={17} /> Get Firm Quote
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
